import { ActivityIndicator, Pressable, Text, View } from "react-native";
import {
  formatDayBadge,
  formatDurationLabel,
  formatShortDayLabel,
  monthLabel,
  type Session,
  type WorkoutMonthSection,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { SelectMark } from "../primitives/SelectMark";
import { useHover } from "../useHover";
import type { WorkoutsStyles } from "./workoutsStyles";

type Tr = (k: string, opts?: Record<string, unknown>) => string;

// Presentational row + month-header for WorkoutsView's SectionList. All state
// + handlers arrive via props; these only render. Shared by the mobile screen
// (tap → the summary, ↻ repeat) and the web-rnw console (tap → the detail
// pane). #368: a hold starts the selection (it used to delete); while
// selecting, a tap adds or removes the row and its ring shows its number.

export function WorkoutListRow({
  item,
  selected,
  selectable = true,
  opening,
  repeating,
  onSelect,
  onRepeat,
  selecting = false,
  order,
  onLongPress,
  styles,
  theme,
  t,
  locale,
}: {
  item: Session;
  /** The web's master-detail highlight; ignored while selecting. */
  selected: boolean;
  /**
   * Whether tapping the row does anything. Mobile = true (→ the summary);
   * the coach's read-only list passes false.
   */
  selectable?: boolean;
  opening: boolean;
  repeating: boolean;
  /** The tap: open it, or (selecting) add or remove it. */
  onSelect: (id: string) => void;
  /** Repeat affordance (mobile only); omit on web → no ↻ column. */
  onRepeat?: (id: string) => void;
  /** #368: the diary is selecting: a ring at the left, no ↻. */
  selecting?: boolean;
  /** The row's place in the selection (1-based); absent: not picked. */
  order?: number;
  /** A hold: starts the selection on this row. */
  onLongPress?: (id: string) => void;
  styles: WorkoutsStyles;
  theme: UiTheme;
  t: Tr;
  locale: string;
}) {
  // Web-only: hover reveals the full name in a bubble (uniform single-line
  // rows can't show a long name inline). onHoverIn never fires on touch, so
  // `hovered` stays false on mobile and the bubble never renders there.
  const { hovered: hoverKey, hoverProps } = useHover();
  const hovered = hoverKey !== null;
  const badge = formatDayBadge(item.startedAt, locale);
  const duration = formatDurationLabel(item.startedAt, item.endedAt);
  const name = item.name ?? t("mobile.workouts.fallbackName");
  const imported = item.source === "healthconnect";
  const importedLabel = t("mobile.workouts.importedBadge", {
    defaultValue: "Imported",
  });
  // Screen readers announce a bare Pressable as "button" only; compose the
  // workout name + day badge so each row is distinguishable in a long list.
  // Append provenance so the "Imported" chip isn't a sighted-only cue.
  // An auto name already ends with its short date: don't say the day twice.
  const short = formatShortDayLabel(item.startedAt, locale);
  const named = short && name.endsWith(short)
    ? name
    : `${name}, ${badge.day} ${badge.dow}`;
  const picked = selecting ? order !== undefined : selected;
  const rowLabel = [
    named,
    ...(imported ? [importedLabel] : []),
    ...(order !== undefined ? [t("mobile.workouts.dayN", { n: order })] : []),
  ].join(", ");
  const body = (
    <>
      {selecting ? <SelectMark theme={theme} checked={order !== undefined} order={order} /> : null}
      <View style={styles.rowDateBlock}>
        <Text style={styles.rowDateDay}>{badge.day}</Text>
        <Text style={styles.rowDateDow}>{badge.dow}</Text>
      </View>
      <View style={styles.rowMain}>
        <Text
          style={[styles.rowName, picked && styles.rowNameSelected]}
          numberOfLines={short && name.endsWith(short) ? 3 : 2}
        >
          {name}
        </Text>
        {duration || imported ? (
          <View style={styles.rowSecondaryRow}>
            {duration ? (
              <Text style={styles.rowSecondary} numberOfLines={1}>
                {duration}
              </Text>
            ) : null}
            {imported ? (
              <View style={styles.hcBadge}>
                <Text style={styles.hcBadgeText} accessible={false}>
                  {importedLabel}
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}
        {hovered ? (
          <View style={styles.nameTip} pointerEvents="none">
            <Text style={styles.nameTipText}>{name}</Text>
          </View>
        ) : null}
      </View>
      {opening ? <ActivityIndicator size="small" color={theme.palette.ink3} /> : null}
    </>
  );
  const interactive = selectable || !!onLongPress;
  const holdable = !!onLongPress && !selecting;
  return (
    <View style={[styles.rowWrap, hovered && styles.rowWrapHover]}>
      {interactive ? (
        <Pressable
          // Selecting, a row is one of a set that can be on: a checkbox, and
          // `aria-checked` for the web (RNW drops accessibilityState).
          accessibilityRole={selecting ? "checkbox" : "button"}
          accessibilityLabel={rowLabel}
          accessibilityState={selecting ? { checked: picked } : { selected: picked }}
          {...(selecting ? { "aria-checked": picked } : {})}
          accessibilityHint={holdable ? t("mobile.workouts.holdToSelect") : undefined}
          // The hold has a door for a screen reader: a «Select» action.
          accessibilityActions={holdable ? [{ name: "activate" }, { name: "select", label: t("editorV3.select") }] : undefined}
          onAccessibilityAction={
            holdable
              ? (e) => {
                  if (e.nativeEvent.actionName === "select") onLongPress?.(item.id);
                  else if (selectable) onSelect(item.id);
                }
              : undefined
          }
          style={({ pressed }) => [
            styles.row,
            picked && styles.rowSelected,
            hovered && !picked && styles.rowHover,
            pressed && styles.rowPressed,
          ]}
          onPress={selectable || selecting ? () => onSelect(item.id) : undefined}
          onLongPress={holdable ? () => onLongPress?.(item.id) : undefined}
          delayLongPress={500}
          {...hoverProps(true)}
        >
          {body}
        </Pressable>
      ) : (
        <View
          accessibilityLabel={rowLabel}
          style={[styles.row, picked && styles.rowSelected]}
        >
          {body}
        </View>
      )}
      {onRepeat && !selecting ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("mobile.workouts.repeat")}
          style={({ pressed }) => [
            styles.repeatButton,
            pressed && styles.repeatButtonPressed,
          ]}
          onPress={() => onRepeat(item.id)}
          disabled={repeating || opening}
        >
          {repeating ? (
            <ActivityIndicator size="small" color={theme.palette.brand500} />
          ) : (
            <Icon
              name="refresh"
              size={theme.typography.sizes.md}
              color={theme.palette.ink2}
            />
          )}
        </Pressable>
      ) : null}
    </View>
  );
}

export function MonthSectionHeader({
  section,
  onToggle,
  styles,
  locale,
}: {
  section: WorkoutMonthSection;
  onToggle: (key: string) => void;
  styles: WorkoutsStyles;
  locale: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={monthLabel(section.key, locale)}
      accessibilityState={{ expanded: !section.collapsed }}
      onPress={() => onToggle(section.key)}
      style={({ pressed }) => [
        styles.sectionHeader,
        pressed && styles.sectionHeaderPressed,
      ]}
    >
      <Text style={styles.sectionTitle}>{monthLabel(section.key, locale)}</Text>
      <View style={styles.sectionRight}>
        <Text style={styles.sectionCount}>{section.total}</Text>
        <Icon
          name={section.collapsed ? "chevron-right" : "chevron-down"}
          size={styles.sectionChevron.fontSize ?? 16}
          color={styles.sectionChevron.color}
        />
      </View>
    </Pressable>
  );
}
